<script>
	/**
	 * [타일] 탭 — "메인 화면에 추가하기 나름"의 주체.
	 * 설계: project-le868/launcher-page-design.md §2-1 · §2-2 · §3-3
	 *
	 * ⭐ 런처의 확장축은 **여기 하나**다. 셸은 고정이고, 모듈이 늘어나는 건 타일이 는다는 뜻이다.
	 * ⚠️ 이 탭이 만지는 건 서버 TILES 칸 하나다. 저장은 배열을 통째로 올린다
	 *    (항목이 아니라 순서까지가 저작 내용이라 부분 갱신이 의미가 없다).
	 */
	import { createEventDispatcher, onMount } from "svelte";
	import { PRConnect } from "@projectroom/ui-lib";
	import { Section, Input, Textarea, Select, Button, Label, Switch } from "../../lib/ui";
	import ImagePicker from "../chat-builder/ImagePicker.svelte";
	import { botAssetFolder } from "../chat-builder/imageAssetApi.js";
	import {
		PRESENTATIONS,
		TILE_AREAS,
		BUILTIN_SCREENS,
		RANKING_SOURCES,
		RANKING_PERIODS,
		newRanking,
		newTile,
	} from "./launcherSlots.js";

	export let bot;
	/** 서버 TILES 칸(배열). */
	export let tiles = [];
	/** assetId → URL. 아이콘 썸네일용(저장하지 않는다). */
	export let iconUrls = {};
	/** [경제] 탭에서 정한 재화 이름. 비용 칸 옆에 보여주기만 한다(저장하지 않는다). */
	export let currencyLabel = "";
	/** [레시피] 탭이 만든 레시피들 — native 타일이 고를 대상. */
	export let recipes = [];
	/**
	 * [신원] 탭이 정의한 받을 정보 칸들 — "꼭 받아야 하는 정보"(gate.requireProfile)가 고를 대상.
	 * ⚠️ 저장하지 않는다(읽기만). 선언은 IDENTITY 한 곳이고, 타일은 **그 키를 가리킬** 뿐이다.
	 */
	export let profileFields = [];
	/** [신원] '정보 입력 받기'. 꺼져 있으면 '꼭 받아야 하는 정보'를 골라도 묻지 않는다(안내를 띄운다). */
	export let collectProfile = true;

	const dispatch = createEventDispatcher();

	$: list = Array.isArray(tiles) ? tiles : [];
	const emit = (next) => dispatch("change", next);

	/* ── 랭킹보드 설정 ─────────────────────────────────────────────────────
	   ⚠️ 설정은 **타일 안에** 산다(`tile.ranking`) — 랭킹보드 타일이 곧 그 표 한 장이라, 한 런처에
	      "주간 게임 랭킹"과 "전체 재화 랭킹"을 각각 타일로 둘 수 있다. */
	const rankingOf = (t) => ({ ...newRanking(), ...(t.ranking || {}) });
	const patchRanking = (t, fields) => patch(t.id, { ranking: { ...rankingOf(t), ...fields } });
	/** 대상으로 고를 수 있는 게임 = **우리 봇에 연결된** 타일(표시 방식은 상관없다 — link든 embed든
	    신원 티켓이 붙으므로 같은 게임 기록으로 쌓인다). */
	$: gameTiles = (tiles || []).filter(
		(t) => t && t.linkBotId && (t.presentation === "link" || t.presentation === "embed"),
	);
	const needsGame = (src) => !!RANKING_SOURCES.find((x) => x.value === src)?.needsGame;

	const patch = (id, fields) =>
		emit(list.map((t) => (t.id === id ? { ...t, ...fields } : t)));

	const add = () => {
		const t = newTile();
		emit([...list, t]);
		openId = t.id;
	};
	const remove = (id) => emit(list.filter((t) => t.id !== id));
	const move = (index, delta) => {
		const to = index + delta;
		if (to < 0 || to >= list.length) return;
		const next = list.slice();
		const [item] = next.splice(index, 1);
		next.splice(to, 0, item);
		emit(next);
	};

	// 한 번에 하나만 펼친다 — 타일이 늘어도 목록이 한눈에 들어오게.
	let openId = null;

	/* ── 연결할 우리 봇 목록 ───────────────────────────────────────────────
	 * ⭐ 주소를 손으로 적는 대신 **우리 봇을 고르면** 런처가 로그인 신원을 함께 넘긴다
	 *    (게임에서 기기를 바꿔도 기록이 이어진다). 손으로 적은 주소에는 신원을 안 붙인다 —
	 *    외부 주소였다면 사용자 신원이 통째로 남의 서버로 가기 때문이다.
	 */
	let linkables = [];
	onMount(() => {
		PRConnect.send("/api/Bot/getBotListById", {}, (data) => {
			if (data.header.status !== "success") return;
			linkables = (data.result || []).filter(
				(b) => b.id !== bot.id && String(b.pageType || "").toUpperCase() === "GAME",
			);
		});
	});

	/* ── 아이콘 ─────────────────────────────────────────────────────────── */
	let pickerOpen = false;
	let pickingId = null;
	$: pickingTile = list.find((t) => t.id === pickingId) || null;

	const openPicker = (id) => {
		pickingId = id;
		pickerOpen = true;
	};
	const onPick = (e) => {
		if (!pickingId) return;
		const { assetId, url } = e.detail || {};
		iconUrls = { ...iconUrls, [assetId]: url };
		patch(pickingId, { iconAssetId: assetId });
		pickingId = null;
	};

	/**
	 * 아이콘을 **비운다**(잘못 넣었을 때 되돌리는 길).
	 * ⚠️⚠️ `onPick` 의 짝이다 — 넣는 길만 있으면 잘못 고른 아이콘을 영영 못 치운다.
	 *    아이콘 없는 타일은 글자만 나오는 **정상 상태**라, "안 넣음"이 의미 있는 선택이다.
	 */
	const clearIcon = (t) => patch(t.id, { iconAssetId: null });

	const setGate = (t, key, value) => patch(t.id, { gate: { ...(t.gate || {}), [key]: value } });

	/**
	 * "꼭 받아야 하는 정보" 한 칸을 켜고 끈다(`gate.requireProfile` 배열).
	 *
	 * ⭐ 이게 켜져 있으면 서버가 비어 있는 칸을 찾아 `reason:'profile'` 로 막고, 런타임이
	 *    **개인정보 안내 화면**을 띄운다(launcherGate 2단계 → App.tsx). 켜지 않으면 그 화면은
	 *    설정에서 직접 들어가는 길밖에 없다.
	 * ⚠️ **빈 배열은 아예 지운다**(undefined). 빈 배열을 남기면 서버 `needsIdentity` 판정이
	 *    `length > 0` 이라 통과하긴 하지만, 저작 JSON 에 뜻 없는 흔적이 남는다.
	 */
	const toggleRequired = (t, key) => {
		const cur = Array.isArray(t.gate?.requireProfile) ? t.gate.requireProfile : [];
		const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
		setGate(t, "requireProfile", next.length ? next : undefined);
	};

	const areaLabel = (v) => TILE_AREAS.find((a) => a.value === v)?.label || v;
	const presLabel = (v) => PRESENTATIONS.find((p) => p.value === v)?.label || v;
	$: hintOf = (v) => PRESENTATIONS.find((p) => p.value === v)?.hint || "";
</script>

<div class="tw-flex tw-flex-col tw-gap-6">
	<Section
		title="타일"
		description="홈 화면에서 누를 수 있는 것들입니다. 게임·채팅·운세 무엇이든 타일 하나로 붙입니다."
	>
		<div class="tw-flex tw-flex-col tw-gap-3">
			{#if list.length === 0}
				<p class="tw-py-6 tw-text-center tw-text-sm tw-text-muted-foreground">
					아직 타일이 없습니다. 아래 [+ 타일 추가]로 시작하세요.
				</p>
			{/if}

			{#each list as t, i (t.id)}
				<div class="tw-rounded-lg tw-border tw-border-border">
					<!-- 접힌 줄: 아이콘 · 이름 · 자리/방식 · 순서 -->
					<div class="tw-flex tw-items-center tw-gap-3 tw-p-3">
						<div class="icon-slot tw-shrink-0">
							<button
								type="button"
								class="icon-thumb"
								on:click={() => openPicker(t.id)}
								title="아이콘 고르기"
							>
								{#if t.iconAssetId && iconUrls[t.iconAssetId]}
									<img src={iconUrls[t.iconAssetId]} alt="" />
								{:else}
									<span class="tw-text-[10px] tw-text-muted-foreground">아이콘</span>
								{/if}
							</button>
							{#if t.iconAssetId}
								<!-- ⚠️ 접힌 줄 전체가 '펼치기' 버튼 옆에 있다 — ✕ 는 **형제**로 두고 겹쳐 놓는다
								     (버튼 안에 버튼을 넣으면 HTML 이 깨지고 클릭이 둘 다 먹는다). -->
								<button
									type="button"
									class="icon-clear"
									title="아이콘 비우기"
									aria-label="아이콘 비우기"
									on:click={() => clearIcon(t)}
								>✕</button>
							{/if}
						</div>
						<button
							type="button"
							class="tw-min-w-0 tw-flex-1 tw-text-left"
							on:click={() => (openId = openId === t.id ? null : t.id)}
						>
							<div class="tw-truncate tw-text-sm tw-font-medium">
								{t.label || "(이름 없음)"}
							</div>
							<div class="tw-mt-0.5 tw-text-xs tw-text-muted-foreground">
								{areaLabel(t.area)} · {presLabel(t.presentation)}
							</div>
						</button>
						<div class="tw-flex tw-shrink-0 tw-items-center tw-gap-1">
							<button
								type="button"
								class="tw-rounded tw-px-1.5 tw-py-1 tw-text-xs tw-text-muted-foreground hover:tw-bg-muted disabled:tw-opacity-30"
								disabled={i === 0}
								on:click={() => move(i, -1)}
								aria-label="위로">▲</button
							>
							<button
								type="button"
								class="tw-rounded tw-px-1.5 tw-py-1 tw-text-xs tw-text-muted-foreground hover:tw-bg-muted disabled:tw-opacity-30"
								disabled={i === list.length - 1}
								on:click={() => move(i, 1)}
								aria-label="아래로">▼</button
							>
						</div>
					</div>

					{#if openId === t.id}
						<div class="tw-flex tw-flex-col tw-gap-3 tw-border-t tw-border-border tw-p-3">
							<!-- ⭐ 한 줄 입력(<input>)이었다 — 그래서 **개행을 넣을 길이 아예 없었다**.
							     버튼 문구는 "오늘의 행운빵\n받기" 처럼 저작자가 줄을 끊어야 모양이 잡힌다
							     (런타임 .lc-tile / .lc-rail-item 이 `white-space: pre-line` 으로 받는다).
							     ⚠️ 줄바꿈은 **런타임이 받아줘야** 뜻이 있다 — 둘 중 하나만 고치면 여전히
							        한 줄로 붙는다(여기만 고쳐서 한 번 헛바퀴 돈 자리다).
							     ⚠️ 엔터가 먹히도록 rows 를 2 로 둔다. 1 이면 칸이 한 줄짜리로 보여서
							        저작자가 엔터를 **쓸 생각을 안 한다.** -->
							<div class="tw-flex tw-items-start tw-gap-3">
								<Label class="tw-w-24 tw-shrink-0 tw-pt-2 tw-text-sm">이름</Label>
								<div class="tw-min-w-0 tw-flex-1">
									<Textarea
										rows="2"
										class="tw-min-h-0"
										placeholder={"오늘의 운세\n받기"}
										value={t.label}
										on:input={(e) => patch(t.id, { label: e.target.value })}
									/>
									<p class="tw-mt-1 tw-text-xs tw-text-muted-foreground">
										엔터로 줄을 끊으면 버튼에서도 그 자리에서 줄이 바뀝니다.
									</p>
								</div>
							</div>

							<div class="tw-flex tw-items-center tw-gap-3">
								<Label class="tw-w-24 tw-shrink-0 tw-text-sm">놓을 자리</Label>
								<Select
									class="tw-flex-1"
									value={t.area}
									on:change={(e) => patch(t.id, { area: e.target.value })}
								>
									{#each TILE_AREAS as a (a.value)}
										<option value={a.value}>{a.label}</option>
									{/each}
								</Select>
							</div>

							<div class="tw-flex tw-items-center tw-gap-3">
								<Label class="tw-w-24 tw-shrink-0 tw-text-sm">여는 방법</Label>
								<Select
									class="tw-flex-1"
									value={t.presentation}
									on:change={(e) => patch(t.id, { presentation: e.target.value })}
								>
									{#each PRESENTATIONS as p (p.value)}
										<option value={p.value}>{p.label}</option>
									{/each}
								</Select>
							</div>
							<p class="tw-pl-[108px] tw-text-xs tw-text-muted-foreground">{hintOf(t.presentation)}</p>

							<!-- ⚠️ 방식을 바꿔도 **안 쓰는 칸을 지우지 않는다** — 되돌렸을 때 값이 날아가지 않게
							     숨기기만 한다(서버·런타임도 같은 규약). -->
							{#if t.presentation === "link"}
								<div class="tw-flex tw-items-center tw-gap-3">
									<Label class="tw-w-24 tw-shrink-0 tw-text-sm">우리 게임</Label>
									<Select
										class="tw-flex-1"
										value={t.linkBotId || ""}
										on:change={(e) => patch(t.id, { linkBotId: e.target.value })}
									>
										<option value="">— 고르지 않음 (주소 직접 입력) —</option>
										{#each linkables as b (b.id)}
											<option value={b.id}>{b.title || b.id}</option>
										{/each}
									</Select>
								</div>
								{#if t.linkBotId}
									<p class="tw-pl-[108px] tw-text-xs tw-text-muted-foreground">
										⭐ 주소는 자동으로 채워지고, <strong>로그인한 사람의 신원이 함께 전달</strong>됩니다 —
										기기를 바꿔도 게임 기록이 이어집니다.
									</p>
								{:else}
									<div class="tw-flex tw-items-center tw-gap-3">
										<Label class="tw-w-24 tw-shrink-0 tw-text-sm">주소</Label>
										<Input
											class="tw-flex-1"
											placeholder="https://…"
											value={t.url}
											on:input={(e) => patch(t.id, { url: e.target.value })}
										/>
									</div>
									<p class="tw-pl-[108px] tw-text-xs tw-text-muted-foreground">
										⚠️ 직접 적은 주소에는 <strong>신원이 전달되지 않습니다</strong>(외부 사이트로 샐 수 있어서).
									</p>
								{/if}
								<div class="tw-flex tw-items-center tw-gap-2 tw-pl-[108px] tw-text-sm">
									<Switch
										checked={t.target === "blank"}
										on:click={() =>
											patch(t.id, { target: t.target === "blank" ? "self" : "blank" })}
										aria-label="새 창으로 열기"
									/>
									새 창으로 열기
								</div>
							{:else if t.presentation === "embed"}
								<!-- ⭐ embed 도 **우리 봇을 고를 수 있다**. 예전엔 손으로 적은 주소뿐이라,
								     같은 게임을 iframe 으로 바꾸는 순간 신원이 사라졌다(티켓이 안 붙어
								     무기명으로 떨어지고 점수가 런처 계정에 안 쌓인다). -->
								<div class="tw-flex tw-items-center tw-gap-3">
									<Label class="tw-w-24 tw-shrink-0 tw-text-sm">우리 모듈</Label>
									<Select
										class="tw-flex-1"
										value={t.linkBotId || ""}
										on:change={(e) => patch(t.id, { linkBotId: e.target.value })}
									>
										<option value="">— 고르지 않음 (주소 직접 입력) —</option>
										{#each linkables as b (b.id)}
											<option value={b.id}>{b.title || b.id}</option>
										{/each}
									</Select>
								</div>
								{#if t.linkBotId}
									<p class="tw-pl-[108px] tw-text-xs tw-text-muted-foreground">
										⭐ 주소는 자동으로 채워지고, <strong>로그인한 사람의 신원이 함께 전달</strong>됩니다.
										런처를 떠나지 않으므로 닫으면 바로 돌아옵니다.
									</p>
								{:else}
									<div class="tw-flex tw-items-center tw-gap-3">
										<Label class="tw-w-24 tw-shrink-0 tw-text-sm">주소</Label>
										<Input
											class="tw-flex-1"
											placeholder="채팅 주소(chatUrl)"
											value={t.embedUrl}
											on:input={(e) => patch(t.id, { embedUrl: e.target.value })}
										/>
									</div>
									<p class="tw-pl-[108px] tw-text-xs tw-text-muted-foreground">
										⚠️ 직접 적은 주소에는 <strong>신원이 전달되지 않습니다</strong>(외부 사이트로 샐 수 있어서).
									</p>
								{/if}
							{:else if t.presentation === "native"}
								<div class="tw-flex tw-items-center tw-gap-3">
									<Label class="tw-w-24 tw-shrink-0 tw-text-sm">레시피</Label>
									<Select
										class="tw-flex-1"
										value={t.recipeId || ""}
										on:change={(e) => patch(t.id, { recipeId: e.target.value })}
									>
										<option value="">— 고르기 —</option>
										{#each recipes as r (r.id)}
											<option value={r.id}>{r.name || r.id}</option>
										{/each}
									</Select>
								</div>
								{#if recipes.length === 0}
									<p class="tw-pl-[108px] tw-text-xs tw-text-muted-foreground">
										[레시피] 탭에서 먼저 만들어주세요.
									</p>
								{/if}
							{:else}
								<div class="tw-flex tw-items-center tw-gap-3">
									<Label class="tw-w-24 tw-shrink-0 tw-text-sm">화면</Label>
									<Select
										class="tw-flex-1"
										value={t.builtinScreen || ""}
										on:change={(e) => patch(t.id, { builtinScreen: e.target.value })}
									>
										<option value="">— 고르기 —</option>
										{#each BUILTIN_SCREENS as b (b.value)}
											<option value={b.value}>{b.label}</option>
										{/each}
									</Select>
								</div>
								<p class="tw-pl-[108px] tw-text-xs tw-text-muted-foreground">
									{BUILTIN_SCREENS.find((b) => b.value === t.builtinScreen)?.hint || ""}
								</p>

								<!-- ⭐ 랭킹보드는 **화면은 런처가 그리고, 데이터만 저작**한다.
								     두 축(무엇을/언제 것을)과 제목·인원이 전부다. -->
								{#if t.builtinScreen === "ranking"}
									{@const rk = rankingOf(t)}
									<div class="tw-ml-[108px] tw-flex tw-flex-col tw-gap-3 tw-rounded-md tw-border tw-border-border tw-p-3">
										<div class="tw-flex tw-items-center tw-gap-3">
											<Label class="tw-w-20 tw-shrink-0 tw-text-sm">줄 세울 것</Label>
											<Select
												class="tw-flex-1"
												value={rk.source}
												on:change={(e) => patchRanking(t, { source: e.target.value })}
											>
												{#each RANKING_SOURCES as r (r.value)}
													<option value={r.value}>{r.label}</option>
												{/each}
											</Select>
										</div>
										<p class="tw-pl-[92px] tw-text-xs tw-text-muted-foreground">
											{RANKING_SOURCES.find((r) => r.value === rk.source)?.hint || ""}
										</p>

										<!-- ⚠️ 게임 소스는 "어느 게임이냐"가 반드시 따라붙는다. 봇 id 를 손으로 적게
										     하지 않고 **타일을 가리킨다** — 서버가 그 타일의 linkBotId 를 쓴다. -->
										{#if needsGame(rk.source)}
											<div class="tw-flex tw-items-center tw-gap-3">
												<Label class="tw-w-20 tw-shrink-0 tw-text-sm">대상 게임</Label>
												<Select
													class="tw-flex-1"
													value={rk.targetTileId}
													on:change={(e) => patchRanking(t, { targetTileId: e.target.value })}
												>
													<option value="">— 고르기 —</option>
													{#each gameTiles as g (g.id)}
														<option value={g.id}>{g.label || g.id}</option>
													{/each}
												</Select>
											</div>
											{#if gameTiles.length === 0}
												<p class="tw-pl-[92px] tw-text-xs tw-text-muted-foreground">
													연결된 게임 타일이 없습니다. 먼저 "주소로 이동" 타일로 게임을 연결해주세요.
												</p>
											{:else if !rk.targetTileId}
												<p class="tw-pl-[92px] tw-text-xs tw-text-destructive">
													대상을 고르지 않으면 표가 비어서 나옵니다.
												</p>
											{/if}
										{/if}

										<div class="tw-flex tw-items-center tw-gap-3">
											<Label class="tw-w-20 tw-shrink-0 tw-text-sm">기간</Label>
											<Select
												class="tw-flex-1"
												value={rk.period}
												on:change={(e) => patchRanking(t, { period: e.target.value })}
											>
												{#each RANKING_PERIODS as p (p.value)}
													<option value={p.value}>{p.label}</option>
												{/each}
											</Select>
										</div>
										<p class="tw-pl-[92px] tw-text-xs tw-text-muted-foreground">
											{RANKING_PERIODS.find((p) => p.value === rk.period)?.hint || ""}
										</p>
										<!-- ⚠️ 과거는 주차로 쪼갤 수 없다 — 게임 기간 랭킹은 배포 시점부터 쌓인다. -->
										{#if needsGame(rk.source) && rk.period !== "all"}
											<p class="tw-pl-[92px] tw-text-xs tw-text-muted-foreground">
												기간별 게임 기록은 이 기능이 켜진 뒤부터 쌓입니다 — 첫 기간은 비어 있을 수 있어요.
											</p>
										{/if}

										<div class="tw-flex tw-items-center tw-gap-3">
											<Label class="tw-w-20 tw-shrink-0 tw-text-sm">인원</Label>
											<Input
												class="tw-w-24"
												type="number"
												min="1"
												max="100"
												value={rk.limit}
												on:input={(e) => patchRanking(t, { limit: Number(e.target.value) || 20 })}
											/>
											<Label class="tw-w-14 tw-shrink-0 tw-text-sm">단위</Label>
											<Input
												class="tw-flex-1"
												placeholder={rk.source === "wallet" ? currencyLabel || "개" : "점"}
												value={rk.unit}
												on:input={(e) => patchRanking(t, { unit: e.target.value })}
											/>
										</div>
										<p class="tw-pl-[92px] tw-text-xs tw-text-muted-foreground">
											화면 제목은 이 타일의 <strong>이름</strong>이 그대로 쓰이고, 그 아래 기간이
											"10월 첫째주"처럼 붙습니다.
										</p>
									</div>
								{/if}
							{/if}

							<!-- 게이팅. ⭐ 판정·정산은 **서버가 한다**(launcherGate.checkAndConsume) —
							     하루 횟수·비용·로그인·필수 정보가 전부 거기서 갈린다. 런타임의 이 값들은
							     저작 내용이고, 클라이언트는 조건을 스스로 판정하지 않는다(설계 §8). -->
							<details class="tw-mt-1">
								<summary class="tw-cursor-pointer tw-text-sm tw-text-muted-foreground">
									누를 수 있는 조건
								</summary>
								<div class="tw-mt-3 tw-flex tw-flex-col tw-gap-3">
									<div class="tw-flex tw-items-center tw-gap-3">
										<Label class="tw-w-24 tw-shrink-0 tw-text-sm">하루 횟수</Label>
										<Input
											class="tw-w-24"
											type="number"
											min="0"
											placeholder="제한 없음"
											value={t.gate?.dailyLimit ?? ""}
											on:input={(e) =>
												setGate(t, "dailyLimit", e.target.value === "" ? undefined : Number(e.target.value))}
										/>
									</div>
									<div class="tw-flex tw-items-center tw-gap-2 tw-text-sm">
										<Switch
											checked={!!t.gate?.requireLogin}
											on:click={() => setGate(t, "requireLogin", !t.gate?.requireLogin)}
											aria-label="로그인한 사람만"
										/>
										로그인한 사람만
									</div>
									<!-- ⭐ 필수 정보 — 비어 있으면 서버가 막고 런타임이 **개인정보 안내 화면**을 띄운다. -->
									<div class="tw-flex tw-flex-col tw-gap-1.5">
										<Label class="tw-text-sm">꼭 받아야 하는 정보</Label>
										{#if (profileFields || []).filter((f) => f?.key).length === 0}
											<p class="tw-text-xs tw-text-muted-foreground">
												[신원] 탭의 <strong>받을 정보</strong>에 칸을 먼저 만들면 여기서 고를 수 있습니다.
											</p>
										{:else}
											<div class="tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-1.5">
												{#each (profileFields || []).filter((f) => f?.key) as pf (pf.key)}
													<label class="tw-flex tw-items-center tw-gap-1.5 tw-text-sm">
														<input
															type="checkbox"
															checked={(t.gate?.requireProfile || []).includes(pf.key)}
															on:change={() => toggleRequired(t, pf.key)}
														/>
														{pf.label || pf.key}
													</label>
												{/each}
											</div>
											{#if !collectProfile && (t.gate?.requireProfile || []).length > 0}
												<p class="tw-text-xs tw-text-amber-600">
													⚠️ [신원] 탭에서 <strong>정보 입력 받기</strong>가 꺼져 있어 지금은 묻지 않습니다.
												</p>
											{/if}
											<p class="tw-text-xs tw-text-muted-foreground">
												비어 있으면 <strong>개인정보 안내 화면</strong>으로 보냅니다([화면] 탭에서 그 안내를
												씁니다). 로그인하지 않은 사람은 그 기기에 입력해 둔 정보로 판정합니다 — 로그인까지
												받으려면 위의 <strong>로그인한 사람만</strong>을 함께 켜세요.
											</p>
										{/if}
									</div>

									<div class="tw-flex tw-items-center tw-gap-3">
										<Label class="tw-w-24 tw-shrink-0 tw-text-sm">드는 비용</Label>
										<Input
											class="tw-w-24"
											type="number"
											min="0"
											placeholder="0"
											value={t.gate?.cost?.amount ?? ""}
											on:input={(e) =>
												setGate(t, "cost", e.target.value === "" ? undefined : { amount: Number(e.target.value) })}
										/>
										<span class="tw-text-xs tw-text-muted-foreground">
											{currencyLabel || "재화"} (0이면 무료)
										</span>
									</div>
									<div class="tw-flex tw-items-center tw-gap-3">
										<Label class="tw-w-24 tw-shrink-0 tw-text-sm">막혔을 때</Label>
										<Input
											class="tw-flex-1"
											placeholder="오늘은 이미 받았어요. 내일 다시 와줘!"
											value={t.gate?.blockedText || ""}
											on:input={(e) => setGate(t, "blockedText", e.target.value)}
										/>
									</div>
									<p class="tw-text-xs tw-text-muted-foreground">
										조건은 <strong>서버가</strong> 판정합니다. 하루 횟수는 한국 시간 자정에 풀립니다.
										로그인하지 않은 사람은 <strong>기기(브라우저)마다</strong> 셉니다 — 경품 이벤트와 같은 방식이라
										브라우저 기록을 지우면 다시 받을 수 있습니다. 비용이 있으면 로그인이 필요합니다(재화는 계정에 쌓입니다).
										<br />⚠️ '막혔을 때' 문구는 <strong>횟수·재화</strong>로 막힐 때만 보입니다 —
										로그인·필수 정보는 각자의 화면으로 보내므로 문구를 띄우지 않습니다.
									</p>
								</div>
							</details>

							<div class="tw-flex tw-justify-end tw-pt-1">
								<button
									type="button"
									class="tw-text-xs tw-text-destructive tw-underline"
									on:click={() => remove(t.id)}>이 타일 삭제</button
								>
							</div>
						</div>
					{/if}
				</div>
			{/each}

			<div>
				<Button variant="outline" size="sm" on:click={add}>+ 타일 추가</Button>
			</div>
		</div>
	</Section>
</div>

<ImagePicker
	bind:open={pickerOpen}
	chatId={bot.id}
	contextFolder={botAssetFolder(bot)}
	mode="single"
	currentAssetId={pickingTile?.iconAssetId || null}
	on:select={onPick}
/>

<style>
	.icon-thumb {
		width: 44px;
		height: 44px;
		display: flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;
		border-radius: 10px;
		border: 1px dashed hsl(var(--sb-border));
		background: hsl(var(--sb-muted));
		cursor: pointer;
	}
	.icon-thumb img {
		max-width: 100%;
		max-height: 100%;
		object-fit: contain;
	}
	/* ✕ 를 썸네일 모서리에 얹는다(레시피 탭 .pick-clear 와 같은 모양). */
	.icon-slot {
		position: relative;
		display: inline-flex;
	}
	.icon-clear {
		position: absolute;
		top: -5px;
		right: -5px;
		width: 17px;
		height: 17px;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 999px;
		border: 1px solid hsl(var(--sb-border));
		background: hsl(var(--sb-background));
		color: hsl(var(--sb-muted-foreground));
		font-size: 10px;
		line-height: 1;
		padding: 0;
		cursor: pointer;
	}
	.icon-clear:hover {
		color: hsl(var(--sb-destructive));
		border-color: hsl(var(--sb-destructive));
	}
</style>
